import React, { useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useEditMode } from '../context/EditModeContext';

/**
 * EditableText
 * --------------------------------------------------------------------
 * Inline-editable text node. Renders an arbitrary tag (`as`) so the
 * surrounding layout stays identical between view-mode and edit-mode.
 *
 * Props:
 *   value       string         - current text
 *   onChange    (next) => void - called on blur if text changed
 *   multiline   boolean        - if true, Enter inserts newline; else blurs
 *   as          string|Comp    - tag to render (default 'span')
 *   className   string
 *   placeholder string         - shown when value is empty in edit mode
 */
function EditableText({
  value,
  onChange,
  multiline = false,
  as: Tag = 'span',
  className = '',
  placeholder = 'Click to edit…',
  ...rest
}) {
  const { editMode } = useEditMode();
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);

  // Keep DOM text in sync with `value` prop while NOT focused, so external
  // updates (undo/redo, hydration) propagate without clobbering caret.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerText !== (value ?? '')) {
      el.innerText = value ?? '';
    }
  }, [value, editMode]);

  if (!editMode) {
    return (
      <Tag className={className} {...rest}>
        {value}
      </Tag>
    );
  }

  const handleBlur = (e) => {
    const next = e.currentTarget.innerText;
    if (next !== (value ?? '')) {
      onChange && onChange(next);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      e.currentTarget.blur();
    }
  };

  return (
    <span
      className="relative inline-block group/editable align-baseline"
      style={{ width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Tag
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        className={`${className} outline-none focus:ring-2 focus:ring-indigo-400/60 focus:rounded-sm ring-offset-1 ring-offset-white hover:outline hover:outline-1 hover:outline-dashed hover:outline-indigo-400/70 cursor-text empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400`}
        {...rest}
      />
      {hovered && (
        <span
          aria-hidden
          className="pointer-events-none absolute -top-2 -right-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-indigo-500 text-white shadow-sm"
        >
          <Pencil size={12} />
        </span>
      )}
    </span>
  );
}

export default EditableText;
